import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUp,
  BookOpen,
  Check,
  FileBarChart2,
  LoaderCircle,
  Settings2,
  X,
} from 'lucide-react';
import { overlay } from 'overlay-kit';
import { type KeyboardEvent, useMemo, useRef, useState } from 'react';

import useFlexibleTextarea from '@/components/Inputs/useFlexibleTextarea';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import AMP_KEYS from '@/configs/amp-track-keys';
import {
  getHeySynclyItemTypeByChatMode,
  HEY_SYNCLY_MESSAGE_TYPE,
  HEY_SYNCLY_STATUS,
  type HeySynclyChat,
  type HeySynclyChatMode,
  heySynclyPromptLibraryListQueryOptions,
} from '@/entities/hey-syncly';
import { errorHandler } from '@/error-handler';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import StructuredFilterForInsight from '@/features/StructuredFilter/StructuredFilterForInsight';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { cn } from '@/lib/cn';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from '@/shared/ui';
import type { DateRangePickerValue } from '@/shared/ui/date-range-picker';
import { getDateRangeFromValue } from '@/shared/ui/date-range-picker/get-date-range-from-value';
import { PresetName } from '@/shared/ui/date-range-picker/get-preset-date-range';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { FEEDBACK_COLUMNS, SOURCE_COLUMNS } from '@/types/filterKeyConst';
import keymap from '@/utils/keymap';
import { track } from '@/utils/track/track';
import { isEmptyString } from '@/utils/util';

import { useHeySynclyPageStore } from '../../../model/hey-syncly-page-store';
import { getLatestChatFilterSnapshot } from '../../../utils/chat-filter';
import {
  MAX_CUSTOM_PROMPT_COUNT,
  PromptLibraryDialog,
  type PromptLibraryItem,
  SavedPromptsSection,
} from '../prompt-library';
import {
  buildSubmittedFilterState,
  type SubmittedFilterState,
  useChatInputFilterState,
} from './use-chat-input-filter-state';

type Props = {
  chat: HeySynclyChat | null;
  chatMode: HeySynclyChatMode;
  onChatModeChange: (nextMode: HeySynclyChatMode) => void;
};

const initialDateRange: DateRangePickerValue = {
  type: 'preset',
  presetName: PresetName.LAST30,
};

type PromptLibraryOverlayDialogProps = {
  onExitComplete?: () => void;
  onOpenChange: (open: boolean) => void;
  onSelectPrompt: (item: PromptLibraryItem) => void;
  open: boolean;
  workspaceId: string;
};

const PromptLibraryOverlayDialog = ({
  onExitComplete,
  onOpenChange,
  onSelectPrompt,
  open,
  workspaceId,
}: PromptLibraryOverlayDialogProps) => {
  const promptLibraryQuery = useQuery(
    heySynclyPromptLibraryListQueryOptions(workspaceId, {
      enabled: !!workspaceId,
    }),
  );

  return (
    <PromptLibraryDialog
      isLoading={promptLibraryQuery.isLoading}
      items={promptLibraryQuery.data ?? []}
      onExitComplete={onExitComplete}
      onOpenChange={onOpenChange}
      onSelectPrompt={onSelectPrompt}
      open={open}
      workspaceId={workspaceId}
    />
  );
};

const ChatInput = (props: Props) => {
  const { parseStructuredFilter } = useStructuredFilter();
  const ref = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const askSubmit = useHeySynclyStore((state) => state.askSubmit);
  const initialFilter = useHeySynclyStore((state) => state.initialFilter);
  const selectedChatAlias = useHeySynclyPageStore(
    (state) => state.currentChatAlias,
  );
  const onChatSelect = useHeySynclyPageStore((state) => state.onChatSelect);
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const { chat, chatMode, onChatModeChange } = props;
  const workspaceId = currWorkspace?.alias ?? '';
  const loadedChatAlias = chat?.alias ?? null;
  const hasActiveChat = selectedChatAlias !== null;
  const isPending = chat?.status === HEY_SYNCLY_STATUS.PENDING;
  const historyFilterSnapshot = useMemo(
    () => getLatestChatFilterSnapshot(chat),
    [chat],
  );
  const [inputValue, setInputValue] = useState('');
  const [selectedPromptLibraryId, setSelectedPromptLibraryId] = useState<
    string | null
  >(null);
  const [submittedFilterState, setSubmittedFilterState] =
    useState<SubmittedFilterState | null>(null);

  const { dataFilter, setDataFilter, dateRangeValue, setDateRangeValue } =
    useChatInputFilterState({
      selectedChatAlias,
      loadedChatAlias,
      initialDateRange,
      historyFilterSnapshot,
      initialFilter,
      submittedFilterState,
      parseStructuredFilter,
    });

  const promptLibraryQuery = useQuery(
    heySynclyPromptLibraryListQueryOptions(workspaceId, {
      enabled: !!workspaceId,
    }),
  );
  const promptLibraryItems = useMemo(
    () => promptLibraryQuery.data ?? [],
    [promptLibraryQuery.data],
  );
  const customPromptCount = useMemo(
    () => promptLibraryItems.filter((item) => !item.is_default).length,
    [promptLibraryItems],
  );
  const canCreatePrompt = customPromptCount < MAX_CUSTOM_PROMPT_COUNT;
  const createPromptDisabledReason =
    'You can save up to 30 prompts. Delete an existing prompt to add a new one.';

  useFlexibleTextarea(
    {
      initFocus: true,
      value: inputValue,
      minHeight: 24,
    },
    ref,
  );

  const onTextAreaKeyDownHandler = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    if (e.nativeEvent.isComposing) return;

    if (keymap.isEnter(e)) {
      if (e.shiftKey) {
        return;
      }

      e.preventDefault();
      void onSubmitHandler();
    }
  };

  const onSubmitHandler = async () => {
    if (isPending || isEmptyString(inputValue)) return;
    const question = inputValue.trim();
    const filter = structuredFilterUtils.buildStructuredFilter(dataFilter);
    const sFilter = new SFilter(filter);

    const dateRange = getDateRangeFromValue(dateRangeValue);
    const startDate = dateRange.from;
    const endDate = dateRange.to.addVal(1, 'day').startOf('day').tz();

    sFilter.setDateFilter({
      start_date: startDate.toJSON(),
      end_date: endDate.toJSON(),
    });

    const payload = sFilter.get();

    const displayingFilters = [
      ...dataFilter,
      {
        columnKey: FEEDBACK_COLUMNS.DATE_START,
        filterValues: [
          { filterKey: startDate.toJSON(), filterName: startDate.toJSON() },
        ],
      },
      {
        columnKey: FEEDBACK_COLUMNS.DATE_END,
        filterValues: [
          { filterKey: endDate.toJSON(), filterName: endDate.toJSON() },
        ],
      },
    ];

    try {
      const filterKeys = Object.keys(payload || {}).join(',');
      const lastAnswerMessage = chat?.messages
        .filter((message) => message.type === HEY_SYNCLY_MESSAGE_TYPE.ANSWER)
        .pop();

      void track(AMP_KEYS.HEY_SYNCLY_QUESTION_SUBMIT, {
        chat_alias: chat?.alias || null,
        previous_prompt: lastAnswerMessage?.text || null,
        filter_keys: filterKeys,
        prompt: question,
      });

      const res = await askSubmit({
        question,
        chat_alias: loadedChatAlias,
        filter: payload || {},
        filter_string: JSON.stringify(displayingFilters),
        item_type: getHeySynclyItemTypeByChatMode(chatMode),
        prompt_library_id:
          selectedPromptLibraryId && question.length > 0
            ? selectedPromptLibraryId
            : null,
      });

      void queryClient.invalidateQueries({
        queryKey: heySynclyPromptLibraryListQueryOptions(workspaceId).queryKey,
      });

      setInputValue('');
      setSelectedPromptLibraryId(null);
      setSubmittedFilterState(
        buildSubmittedFilterState({
          chatAlias: res.alias,
          dateRangeValue,
          dataFilter,
        }),
      );

      if (res.alias && res.alias !== selectedChatAlias) {
        onChatSelect(res.alias);
      }
    } catch (error) {
      const msg = errorHandler(error, 'Failed to ask Hey Syncly');
      toast(msg, 'fail');
    }
  };

  const quickFilterKeys = useMemo(() => [SOURCE_COLUMNS.SOURCE_TYPE], []);
  const canSubmit = !isPending && !isEmptyString(inputValue);

  const applyPrompt = (prompt: string) => {
    setInputValue(prompt);
    requestAnimationFrame(() => ref.current?.focus());
  };

  const handlePromptSelect = (item: PromptLibraryItem) => {
    setSelectedPromptLibraryId(item.id);
    applyPrompt(item.prompt);
  };

  const onChangeChatMode = (nextMode: HeySynclyChatMode) => {
    if (nextMode === 'REPORT') {
      void track(AMP_KEYS.HEY_SYNCLY_TOOL_SELECT, {
        type: 'DOC',
      });
    }

    onChatModeChange(nextMode);
  };

  const openPromptLibrary = () => {
    if (!workspaceId) {
      return;
    }

    overlay.open(({ isOpen, close, unmount }) => (
      <PromptLibraryOverlayDialog
        onOpenChange={close}
        onSelectPrompt={(item) => {
          handlePromptSelect(item);
          close();
        }}
        open={isOpen}
        workspaceId={workspaceId}
        onExitComplete={unmount}
      />
    ));
  };

  return (
    <div className="relative flex flex-col items-center w-full gap-15">
      <div
        className={cn(
          'relative flex min-h-36 w-full flex-col gap-3 rounded-3.5xl border border-dune-300 bg-white px-4 py-3 shadow-[0_0_6px_0_rgba(0,0,0,0.06)] transition-colors duration-200',
          'has-[textarea:focus]:border-dune-400',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <StructuredFilterForInsight
            dateRange={dateRangeValue}
            setDateRange={setDateRangeValue}
            values={dataFilter}
            onChange={setDataFilter}
            clearFilter={() => {
              setDataFilter([]);
              setDateRangeValue(initialDateRange);
            }}
            quickFilterKeys={quickFilterKeys}
          />
        </div>
        <div className="flex items-end justify-between">
          <div className="relative flex flex-1 items-center pl-0.5">
            <textarea
              id="hey-syncly-chat-input"
              ref={ref}
              placeholder="What do you want to know?"
              className="w-full p-2 resize-none no-scrollbar max-h-39 focus:outline-none"
              value={inputValue}
              rows={1}
              onChange={(e) => {
                const nextValue = e.target.value;
                setInputValue(nextValue);

                if (!nextValue.trim()) {
                  setSelectedPromptLibraryId(null);
                }
              }}
              onKeyDown={onTextAreaKeyDownHandler}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 h-9">
          <div className="flex items-center gap-1">
            <Tooltip content="Prompt library" side="top">
              <span>
                <Button
                  className="rounded-full"
                  disabled={!workspaceId}
                  size="icon"
                  variant="outline"
                  onClick={openPromptLibrary}
                >
                  <BookOpen className="size-3.5" />
                </Button>
              </span>
            </Tooltip>
            <div className="relative ml-0.5 h-9 min-w-[112px]">
              <AnimatePresence initial={false} mode="wait">
                {chatMode === 'GENERAL' ? (
                  <motion.div
                    key="general-mode"
                    className="absolute inset-0"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                  >
                    <div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="flex h-9 items-center gap-1.5 rounded-full px-3 text-card-foreground transition-all select-none hover:bg-accent hover:text-foreground active:scale-95 active:bg-accent/80 data-[state=open]:bg-accent data-[state=open]:text-foreground"
                            type="button"
                          >
                            <Settings2 className="size-4" />
                            <span className="text-sm leading-none font-medium">
                              Tools
                            </span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="min-w-[200px] rounded-lg border border-dune-300 p-1.5 shadow-md"
                        >
                          <DropdownMenuItem
                            className="flex items-start gap-2.5 rounded-md px-3 py-2.5"
                            onClick={() => onChangeChatMode('REPORT')}
                          >
                            <FileBarChart2 className="mt-0.5 size-4 shrink-0 text-card-foreground" />
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-foreground">
                                Report
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Generate a shareable report document
                              </span>
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="report-mode"
                    className="absolute inset-0 flex items-center gap-1 ml-1"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <span>
                          <Tooltip content="Tools">
                            <span>
                              <Button
                                className="rounded-full"
                                variant="ghost"
                                size="icon-sm"
                              >
                                <Settings2 className="size-4" />
                              </Button>
                            </span>
                          </Tooltip>
                        </span>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="min-w-[200px] rounded-lg border border-dune-300 p-1.5 shadow-md"
                      >
                        <DropdownMenuItem
                          className="flex items-start gap-2.5 rounded-md px-3 py-2.5"
                          onClick={() => onChangeChatMode('GENERAL')}
                        >
                          <FileBarChart2 className="mt-0.5 size-4 shrink-0 text-card-foreground" />
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-foreground">
                              Report
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Generate a shareable report document
                            </span>
                          </div>
                          <Check className="size-5 text-blue-400" />
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Tooltip content="Remove report format">
                      <button
                        className="group/report flex h-9 items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 pl-2.5 pr-3 text-blue-600 transition-all select-none hover:bg-blue-100 hover:pr-2 active:scale-95"
                        onClick={() => onChangeChatMode('GENERAL')}
                        type="button"
                      >
                        <FileBarChart2 className="size-3.5 shrink-0" />
                        <span className="text-[13px] leading-none font-medium">
                          Report
                        </span>
                        <X className="hidden size-3.5 shrink-0 group-hover/report:block" />
                      </button>
                    </Tooltip>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <Button
            variant="default"
            size="icon"
            className={cn(
              'rounded-full bg-foreground text-white transition-all duration-100 hover:bg-card-foreground',
              canSubmit || isPending
                ? 'opacity-100'
                : 'pointer-events-none !opacity-0',
            )}
            disabled={!canSubmit}
            onClick={() => void onSubmitHandler()}
            type="button"
          >
            <ToggleStage
              status={!isPending}
              fallback={<LoaderCircle className="size-4 animate-spin" />}
            >
              <ArrowUp className="size-4.5" />
            </ToggleStage>
          </Button>
        </div>
      </div>

      {!hasActiveChat ? (
        <SavedPromptsSection
          canCreatePrompt={canCreatePrompt}
          createPromptDisabledReason={createPromptDisabledReason}
          isLoading={promptLibraryQuery.isLoading}
          items={promptLibraryItems}
          onSelectPrompt={handlePromptSelect}
        />
      ) : null}
    </div>
  );
};

export default ChatInput;
